import { useAccountState } from '../../../../redux/reducers/accountReducer';
/* eslint-disable @typescript-eslint/no-explicit-any */
import { DataBarVerticalRegular, DataLineRegular } from '@fluentui/react-icons';
import { Column, PortalSlotContent, Row } from '../../../common';

import { useAccess } from '../../../../auth/useAccess';
import { useUtilities } from '../../../../utilities/useUtilities';
import { MouseEvent, useEffect, useState } from 'react';
import { Case, TimeTrackingEntry, User, UserRef } from '../../../../data/types';
import { Button, Divider, makeStyles, Text, tokens } from '@fluentui/react-components';
import { useCaseServicesContext } from '../Cases/CaseServicesContext';
import { BillableTimePlot } from '../../Insights/BillableInsights/BillableTimePlot';
import { UserDropdown } from '../../../controls/Comboboxes/UserDropdown';
import { FormSection } from '../../../common/FormSection';
import { BillableTimeGauge } from '../../Insights/BillableInsights/BillableTimeGauge';
import { TimePeriodDropdown } from '../../../controls/Comboboxes/TimePeriodDropdown';

const useStyles = makeStyles({
    customLineColor: {
        '::before': {
            border: `1px dashed ${tokens.colorNeutralStroke1}`,
        },
        '::after': {
            border: `1px dashed ${tokens.colorNeutralStroke1}`,
        },
    },
});

export const CaseBillableInsightsPage = () => {
    //Hooks
    const { caseForm, timeTrackingService, caseService, invoiceService } = useCaseServicesContext()!;

    //Vars
    const caseObj = caseForm.values as Case;

    //Hooks
    const { primaryAccount, getUserName } = useAccountState();
    const access = useAccess(primaryAccount?.user);
    const utils = useUtilities();

    //State
    const [period, setPeriod] = useState<string>();
    const [startDateRange, setStartDateRange] = useState<Date | undefined>();
    const [endDateRange, setEndDateRange] = useState<Date | undefined>();
    const [selectedUser, setSelectedUser] = useState<User | UserRef | undefined>();
    const [revenueInfo, setRevenueInfo] = useState<{
        totalRev?: number;
        totalHrs?: number;
        invoicedHrs?: number;
        unInvoicedHrs?: number;
        invoicedRev?: number;
        uninvoicedRev?: number;
        totalExpenses?: number;
    }>();
    const [costInfo, setCostInfo] = useState<{
        totalCost?: number;
        totalBillableHrs?: number;
        totalNonBillableHrs?: number;
        hoursByUser?: any[];
    }>();
    const [invoiceInfo, setInvoiceInfo] = useState<{
        totalInvoiced?: number;
        totalInvoicedFromHours?: number;
        totalInvoicedFromExpenses?: number;
        totalPaid?: number;
    }>();
    const [showAsBar, setShowAsBar] = useState<boolean>(false);

    //Styles
    const styles = useStyles();

    //Logic
    const filterForTime = (entry: TimeTrackingEntry) => {
        const entryDate = new Date(entry.date!);
        if (!startDateRange || !endDateRange) return true;
        return entryDate >= startDateRange && entryDate <= endDateRange!;
    };
    const filterForUser = (entry: TimeTrackingEntry) => {
        return !selectedUser || entry.userId == selectedUser?.userId;
    };
    const isAdminOrLead = () => access.account.isAccountAdmin() || access.cases.isCaseLead(caseObj.caseId);

    //Get Data
    const getUsersToSelectFrom = () => {
        const usersToSelect = caseObj.assignedUsers;
        return usersToSelect;
    };
    const getRevenueInfo = () => {
        const entries = timeTrackingService.billableTimeEntries.filter(filterForTime).filter(filterForUser);

        let totalRev = 0;
        let totalHrs = 0;
        let invoicedHrs = 0;
        let invoicedRev = 0;
        let unInvoicedHrs = 0;
        let uninvoicedRev = 0;

        entries.forEach(async (entry: TimeTrackingEntry) => {
            const caseObj = caseService.findCaseById(entry.caseId);
            const assignedUser = caseObj?.assignedUsers?.find((x) => x.userId == entry.userId);

            if (assignedUser) {
                totalRev += entry.hours * (Number(assignedUser?.clientBillRate) || 0);
                totalHrs += entry.hours;
                if (!!entry.invoiceId) {
                    invoicedHrs += entry.hours;
                    invoicedRev += entry.hours * (Number(assignedUser?.clientBillRate) || 0);
                } else {
                    unInvoicedHrs += entry.hours;
                    uninvoicedRev += entry.hours * (Number(assignedUser?.clientBillRate) || 0);
                }
            }
        });

        return { totalRev, totalHrs, invoicedHrs, unInvoicedHrs, invoicedRev, uninvoicedRev };
    };
    const getCostInfo = () => {
        const entries = timeTrackingService.timeEntries.filter(filterForTime).filter(filterForUser);

        let totalCost = 0;
        let totalBillableHrs = 0;
        let totalNonBillableHrs = 0;
        const hoursByUser: {
            userId: string;
            userName: string;
            billablePay: number;
            nonBillablePay: number;
        }[] = [];

        entries.forEach((entry: TimeTrackingEntry) => {
            const caseObj = caseService.findCaseById(entry.caseId);
            const assignedUser = caseObj?.assignedUsers?.find((x) => x.userId == entry.userId);

            if (assignedUser) {
                const payRate = Number(assignedUser?.userPayRate || assignedUser?.clientBillRate) || 0;

                totalCost += entry.hours * payRate;
                totalBillableHrs += entry.isBillable ? entry.hours : 0;
                totalNonBillableHrs += !entry.isBillable ? entry.hours : 0;

                // Track hours by user
                let userEntry = hoursByUser.find((u) => u.userId == entry.userId);
                if (!userEntry) {
                    userEntry = {
                        userId: entry.userId!,
                        userName: assignedUser.username || '',
                        billablePay: 0,
                        nonBillablePay: 0,
                    };
                    hoursByUser.push(userEntry);
                }

                if (entry.isBillable) {
                    userEntry.billablePay += entry.hours * payRate;
                } else {
                    userEntry.nonBillablePay += entry.hours * payRate;
                }
            }
        });

        return { totalCost, totalBillableHrs, totalNonBillableHrs, hoursByUser };
    };
    const getInvoiceInfo = () => {
        let totalInvoiced = 0;
        let totalInvoicedFromHours = 0;
        let totalInvoicedFromExpenses = 0;
        let totalPaid = 0;

        invoiceService.invoices.forEach((invoice) => {
            const fromHours = (invoice.billableTimeEntries || []).reduce(
                (sum, entry) => sum + (entry.hours || 0) * (entry.rate || 0),
                0,
            );
            const fromExpenses = (invoice.expenses || []).reduce((sum, expense) => sum + Number(expense.amount || 0), 0);
            totalInvoicedFromHours += fromHours;
            totalInvoicedFromExpenses += fromExpenses;
            if (invoice.isPaid) {
                totalPaid += fromHours + fromExpenses;
            }
        });

        totalInvoiced = totalInvoicedFromHours + totalInvoicedFromExpenses;
        return { totalInvoiced, totalInvoicedFromHours, totalInvoicedFromExpenses, totalPaid };
    };

    //UI Events
    const onTimePeriodChange = (startDate: Date, endDate: Date, period?: string) => {
        setStartDateRange(startDate);
        setEndDateRange(endDate);
        setPeriod(period);
    };
    const onChartLineViewClick = (e: MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation();
        setShowAsBar(false);
    };
    const onChartBarViewClick = (e: MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation();
        setShowAsBar(true);
    };
    const onUserSelected = (user?: UserRef) => {
        setSelectedUser(user);
    };

    //Effects
    useEffect(() => {
        setRevenueInfo(getRevenueInfo());
        setCostInfo(getCostInfo());
        setInvoiceInfo(getInvoiceInfo());
    }, [timeTrackingService.timeEntries, selectedUser, startDateRange, endDateRange]);

    useEffect(() => {
        if (!isAdminOrLead()) {
            setSelectedUser(primaryAccount?.user);
        }
    }, [primaryAccount?.user.userId]);

    return (
        <>
            <PortalSlotContent name='caseInsightsPageHeader'>
                <Row width='auto' gap='small'>
                    <Column flex='1 1 auto' vAlign='middle' align='right'>
                        <TimePeriodDropdown caseObj={caseObj} onTimePeriodChange={onTimePeriodChange} fullCaseOption />
                    </Column>
                    <Column flex='1 1 auto' vAlign='middle' align='right'>
                        {isAdminOrLead() && (
                            <UserDropdown
                                title='Filter By User'
                                usersToSelectFrom={getUsersToSelectFrom()}
                                onUserSelected={onUserSelected}
                                includeClearOption
                                clearOptionText='Clear Filter'
                            />
                        )}
                    </Column>
                </Row>
            </PortalSlotContent>
                <Column flex='1' padding='px-2' noGap scroll>
                    <Column height='auto' align='center'>
                        <Row gap='small' width='auto'>
                            <Button
                                icon={<DataLineRegular />}
                                onClick={onChartLineViewClick}
                                data-testid='case-billable-insights-line-view-button'
                            />
                            <Button
                                icon={<DataBarVerticalRegular />}
                                onClick={onChartBarViewClick}
                                data-testid='case-billable-insights-bar-view-button'
                            />
                        </Row>
                    </Column>
                    <Column flex='1' minHeight='15rem' height='auto'>
                        <BillableTimePlot
                            asBar={showAsBar}
                            billableTimeEntries={timeTrackingService.billableTimeEntries}
                            nonBillableTimeEntries={timeTrackingService.nonBillableTimeEntries}
                            filterByUser={selectedUser}
                            rangeStartDate={startDateRange}
                            rangeEndDate={endDateRange}
                        />
                    </Column>
                    <Column height='auto' noGap>
                        <Row padding='px-4 pb-1'>
                            <Column flex='3' padding='px-2'>
                                <Divider className={styles.customLineColor}>
                                    <Row>
                                        {!!period && (
                                            <Text size={200} italic>
                                                Time Period: {period}
                                            </Text>
                                        )}
                                        {!!selectedUser && (
                                            <Text size={200} italic>
                                                User: {getUserName(selectedUser.userId)}
                                            </Text>
                                        )}
                                    </Row>
                                </Divider>
                            </Column>
                            <Column flex='1' padding='pl-3'>
                                {access.invoices.canViewInvoices(caseObj.caseId) && (
                                    <Divider className={styles.customLineColor}>
                                        <Text size={200} italic>
                                            For Entire Case
                                        </Text>
                                    </Divider>
                                )}
                            </Column>
                        </Row>
                        <Row padding='px-4' minHeight='min-content'>
                            <Column flex='1' gap='small'>
                                <FormSection>
                                    <BillableTimeGauge
                                        billableTimeEntries={timeTrackingService.billableTimeEntries}
                                        nonBillableTimeEntries={timeTrackingService.nonBillableTimeEntries}
                                        filterByUser={selectedUser}
                                        rangeStartDate={startDateRange}
                                        rangeEndDate={endDateRange}
                                    />
                                </FormSection>
                            </Column>
                            <Column flex='1'>
                                {isAdminOrLead() ? (
                                    <FormSection height='100%' title='Revenue' gap='small'>
                                        <Column gap='small'>
                                            <Text size={500} weight='bold' style={{ color: tokens.colorBrandForeground1 }}>
                                                {utils.formatCurrency(revenueInfo?.totalRev)}
                                            </Text>
                                            <Row>
                                                <Column noGap width='auto'>
                                                    <Text size={300}>Total hours:</Text>
                                                    <Column noGap align='left' padding='pt-2'>
                                                        <Text size={300}>Invoiced:</Text>
                                                        <Text size={300}>Uninvoiced:</Text>
                                                    </Column>
                                                </Column>
                                                <Column noGap align='right'>
                                                    <Text weight='semibold'>
                                                        {Number(revenueInfo?.totalHrs).toFixed(2)} hrs
                                                    </Text>
                                                    <Column noGap align='right' padding='pt-2'>
                                                        <Text weight='semibold'>
                                                            {utils.formatCurrency(revenueInfo?.invoicedRev)}{' '}
                                                            <Text weight='regular' size={200}>
                                                                {' '}
                                                                ({Number(revenueInfo?.invoicedHrs).toFixed(2)} hrs)
                                                            </Text>
                                                        </Text>
                                                        <Text weight='semibold'>
                                                            {utils.formatCurrency(revenueInfo?.uninvoicedRev)}{' '}
                                                            <Text weight='regular' size={200}>
                                                                {' '}
                                                                ({Number(revenueInfo?.unInvoicedHrs).toFixed(2)} hrs)
                                                            </Text>
                                                        </Text>
                                                    </Column>
                                                </Column>
                                            </Row>
                                        </Column>
                                    </FormSection>
                                ) : (
                                    <FormSection height='100%' title='Earnings' gap='small'>
                                        <Column gap='small'>
                                            <Text
                                                size={500}
                                                weight='bold'
                                                style={{
                                                    color: tokens.colorStatusSuccessBorderActive,
                                                }}
                                            >
                                                {utils.formatCurrency(costInfo?.totalCost)}
                                            </Text>
                                            <Row>
                                                <Column noGap>
                                                    <Text size={300}>Billable hrs:</Text>
                                                    <Text size={300}>Non-billable hrs:</Text>
                                                </Column>
                                                <Column noGap align='right'>
                                                    <Text weight='semibold'>
                                                        {Number(costInfo?.totalBillableHrs).toFixed(2)} hrs
                                                    </Text>
                                                    <Text weight='semibold'>
                                                        {Number(costInfo?.totalNonBillableHrs).toFixed(2)} hrs
                                                    </Text>
                                                </Column>
                                            </Row>
                                        </Column>
                                    </FormSection>
                                )}
                            </Column>
                            <Column flex='1'>
                                {isAdminOrLead() && (
                                    <FormSection height='100%' title='Cost' gap='small'>
                                        <Column gap='small'>
                                            <Text size={500} weight='bold' style={{ color: 'red' }}>
                                                {utils.formatCurrency(costInfo?.totalCost)}
                                            </Text>
                                            <Row>
                                                <Column noGap>
                                                    <Text size={300}>Billable hrs:</Text>
                                                    <Text size={300}>Non-billable hrs:</Text>
                                                </Column>
                                                <Column noGap align='right'>
                                                    <Text weight='semibold'>
                                                        {Number(costInfo?.totalBillableHrs).toFixed(2)} hrs
                                                    </Text>
                                                    <Text weight='semibold'>
                                                        {Number(costInfo?.totalNonBillableHrs).toFixed(2)} hrs
                                                    </Text>
                                                </Column>
                                            </Row>
                                        </Column>
                                    </FormSection>
                                )}
                            </Column>
                            <Column flex='1'>
                                {access.invoices.canViewInvoices(caseObj.caseId) && (
                                    <FormSection height='100%' title='Invoices' gap='small'>
                                        <Column gap='small'>
                                            <Row>
                                                <Column>
                                                    <Text
                                                        size={500}
                                                        weight='bold'
                                                        style={{
                                                            color: tokens.colorBrandForeground1,
                                                        }}
                                                    >
                                                        {utils.formatCurrency(invoiceInfo?.totalInvoiced)}
                                                    </Text>
                                                </Column>
                                                <Column width='auto' vAlign='middle'>
                                                    <Text size={400} weight='semibold'>
                                                        {' '}
                                                        Invoiced
                                                    </Text>
                                                </Column>
                                            </Row>
                                            <Row noGap>
                                                <Column noGap>
                                                    <Text size={300}>Billable Hours:</Text>
                                                    <Text size={300}>Expenses: </Text>
                                                    <Column padding='pt-2' align='left' noGap>
                                                        <Text size={300}>Paid:</Text>
                                                        <Text size={300}>Outstanding:</Text>
                                                    </Column>
                                                </Column>
                                                <Column noGap align='right'>
                                                    <Text weight='semibold'>
                                                        {utils.formatCurrency(invoiceInfo?.totalInvoicedFromHours)}
                                                    </Text>
                                                    <Text weight='semibold'>
                                                        {utils.formatCurrency(invoiceInfo?.totalInvoicedFromExpenses)}
                                                    </Text>
                                                    <Column padding='pt-2' align='right' noGap>
                                                        <Text weight='semibold' style={{ color: 'green' }}>
                                                            {utils.formatCurrency(invoiceInfo?.totalPaid)}
                                                        </Text>
                                                        <Text weight='semibold' style={{ color: 'red' }}>
                                                            {utils.formatCurrency(
                                                                (invoiceInfo?.totalInvoiced || 0) -
                                                                    (invoiceInfo?.totalPaid || 0),
                                                            )}
                                                        </Text>
                                                    </Column>
                                                </Column>
                                            </Row>
                                        </Column>
                                    </FormSection>
                                )}
                            </Column>
                        </Row>
                    </Column>
                </Column>
        </>
    );
};
